import React, {useEffect, useState} from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Linking,
} from 'react-native';
import {Colors} from '../../constants/Colors';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import {formatDate} from '../../utils/formatDate';
import NotificationsService from '../../services/NotificationsService/NotificationsService';
import {useFilterStore} from '../../store/FilterStore';
import {useUserStore} from '../../store/UserStore';
import ProductsService from '../../services/ProductsService/ProductsService';
import SkeletonLoader from '../../components/SkeletonLoader/SkeletonLoader';
import ProductFavoriteService from '../../services/ProductFavoriteService/ProductFavoriteService';
import ProductList from '../Home/components/ProductList/ProductList';
import { useBagStore } from '../../store/BagStore';
import { showInfo } from '../../utils/globalAlert';

interface FavoriteProducts {
  id: string;
  title: string;
  message: string;
  date: string;
  read: boolean;
  route: string;
  image: any;
}


const FavoriteProducts: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);// Estado para o pull-to-refresh

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [catalogData, setCatalogData] = useState<IProduct[] | null>(null);

  const {user} = useUserStore();
  const navigate = useNavigation();


  const { addProduct, updateProductQuantity } = useBagStore();
  const products = useBagStore(state => state.products);


useFocusEffect(React.useCallback(() => {
    fetchFavoriteProducts();
  }, [])
  );

  const fetchFavoriteProducts = async () => {
      setIsLoading(true);
      try {
        const response = await ProductFavoriteService.getFavorites(user?.clientCode || '');
        if(response && response.data && response.data.length > 0) {
            const skus = response.data.map((product: any) => product.sku);
            const products = await CatalogProducts(skus.join(','));
            if(products) {
                setCatalogData(products);
            } else {
                setCatalogData([]);
            }
        } else {
            setCatalogData([]);
        }
      } catch (error) {
        console.error('Error fetching favorite products:', error);
        setCatalogData([]);
      }
    setIsLoading(false);
  };


  const CatalogProducts = async (skus: string) => {
    try {
      const response = await ProductsService.catalogProductsGet(
        user?.clientCode,
        999,
        1,
        undefined,
        undefined,
        undefined,
        undefined,
        skus,
      );

      return response.data?.products;
    } catch (error) {
      console.error(error);
    }
  };

  const goBack = () => {
    navigate.goBack();
  };

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyMessage}>
        Você não tem produtos favoritos no momento.
      </Text>
      <View style={styles.emptyButtons}>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => (navigate as any).navigate(AppRoutes.MAIN_TAB_SCREEN, { screen: 'Home' })}>
          <Text style={styles.primaryButtonText}>Ir para o Catálogo</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const sendCart = async (products: any[]) => {
      const instance = AxiosService.createProductsAxiosInstance();
      const data = products.map(product => ({
        sku: product.sku,
        quantity: product.quantity,
      }));
      const response = await instance.post(
        '/carts',
        {
          client_code: user?.clientCode,
          data,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      if (!response.data.data) {
        console.error('sendCart: No data received from server');
        throw new Error('No data received from server');
      }
    };

const handleAddBag = (product: IProduct, quantity: number) => {
    const emptyBag = products.length === 0;

    const {products: currentProducts} = useBagStore.getState();
    if (currentProducts.find(prod => prod.sku === product.sku)) {
      updateProductQuantity(product.sku, quantity);
      return;
    } else if (quantity > 0) {
      addProduct({
        ...product,
        quantity: quantity,
      });
    } else {
      showInfo('Conecta Brasal', 'Informe a quantidade de itens.');
    }

    if (emptyBag) {
      try {
        sendCart(products);
      } catch (error) {
        console.error('Erro ao enviar o carrinho:', error);
      }
    }
  };

  const renderItem =  ({ item }: { item: IProduct })  => (
      <ProductList
        hideFavoriteButton={true}
        clientCode={user?.clientCode}
        item={item}
        image={item.image}
        title={item.descricao}
        description={
          item.descricao + ' ' + item.complemento.replace('- R$', '')
        }
        onPress={() => openProduct(item)}
        AddBag={(quantity: number) => handleAddBag(item, quantity)}
      />
  );


  return (
    <View style={styles.container}>
      <SearchHeader
        onPress={goBack}
        title="Produtos Favoritos"
        mb={30}
        mt={20}
        ph={10}
      />
      {isLoading ? (
        <SkeletonLoader />
      ) : catalogData.length === 0 ? (
        renderEmptyState()
      ) : (
        <FlatList
          data={catalogData}
          keyExtractor={(item, index) => 'key' + index}
          showsVerticalScrollIndicator={false}
          renderItem={renderItem}
          numColumns={2}
          onEndReachedThreshold={0.1}
          scrollEventThrottle={16}
          ListFooterComponent={
            isLoading ? (
              <SkeletonLoader />
            ) : null
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#fff',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyMessage: {
    fontSize: 16,
    color: Colors.GrayPallete.lightgrayThree,
    textAlign: 'center',
    marginBottom: 20,
  },
  emptyButtons: {
    width: '100%',
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: Colors.RedPallete.red,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginBottom: 10,
    width: '80%',
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: Colors.RedPallete.red,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    width: '80%',
    alignItems: 'center',
  },
  secondaryButtonText: {
    color: Colors.RedPallete.red,
    fontSize: 16,
    fontWeight: 'bold',
  },
  notificationCard: {
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 10,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  avatar: {
    width: 45,
    height: 45,
    marginRight: 12,
    borderRadius: 25,
    color: '#000',
    resizeMode: 'contain',
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
    marginBottom: 2,
  },
  unreadTitle: {
    color: '#E60000',
  },
  message: {
    fontSize: 13,
    color: '#333',
  },
  date: {
    fontSize: 11,
    color: '#999',
    marginTop: 6,
  },
  arrow: {
    fontSize: 20,
    color: '#ccc',
    marginLeft: 8,
    marginTop: 4,
  },
  separator: {
    height: 1,
    backgroundColor: '#eee',
    marginVertical: 10,
  },
  skeletonContainer: {
    padding: 16,
  },
  skeletonCard: {
    height: 80,
    backgroundColor: '#e0e0e0',
    borderRadius: 10,
    marginBottom: 16,
  },
});

export default FavoriteProducts;
